class ProductGallery extends HTMLElement {
  constructor() {
    super();

    this.mobileMatchMedia = window.matchMedia('(max-width: 959px)');
    this.galleryViewerElement = this.querySelector('.product-gallery-viewer');
    this.galleryViewerElementImage = this.galleryViewerElement.querySelector('.product-gallery-viewer-image');
    this.galleryViewerImage = this.galleryViewerElement.querySelector('[data-viewer-image]');
    this.galleryTriggers = this.querySelectorAll('[data-main-image]');
    this.galleryClose = this.querySelector('[data-close]');
    this.galleryMain = this.querySelector('.product-gallery-main');
    this.view360Triggers = document.querySelectorAll('[data-view-360]');
    this.globalBanner = document.querySelector('.shopify-section-group-header-group');

    this.productGalleryThumbnailsHeight = ((32 + 53 + 32) / 2) * -1;

    if (this.view360Triggers.length) {
      this.view360TriggerDesktop = this.view360Triggers[0];
    }

    this.header = document.querySelector('expanded-header');
    this.headerGroup = document.querySelector('.header-group');

    this.thumbnails = this.querySelectorAll('.product-gallery-thumbnails button');
    this.thumbnailMain = this.querySelector('.product-gallery-thumbnails');
    this.activeThumbnail = this.thumbnails[0];

    this.featuredThumb = document.querySelector('[data-open-media]');

    this.galleryVideos = this.querySelectorAll('.product-gallery--video');

    this.isDragging = false;

    this.setupEventListeners();

    this.isViewing360 = false;
    this.imageViewerOpen = false;

    this.galleryPopulated = false;
  }

  setupEventListeners() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this));
    });

    this.galleryTriggers.forEach((galleryTrigger) => {
      galleryTrigger.addEventListener('click', this.openGallery.bind(this));
      galleryTrigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.openGallery(event);
        }
      });
    });

    this.galleryClose.addEventListener('click', this.closeGallery.bind(this));

    this.view360Triggers.forEach((view360Trigger) => {
      view360Trigger.addEventListener('click', this.onViewMedia.bind(this));
    });

    if (this.view360Triggers.length) {
      document.addEventListener('scroll', this.onDocumentScroll.bind(this));
    }

    this.featuredThumb.addEventListener('click', this.onScrollToMedia.bind(this));

    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('touchmove', this.onTouchMove.bind(this));

    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    document.addEventListener('touchend', this.onMouseUp.bind(this));

    this.galleryViewerImage.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.galleryViewerImage.addEventListener('touchstart', this.onTouchStart.bind(this));
    this.galleryViewerImage.addEventListener('click', this.onClick.bind(this));

    this.recalcHeight();

    window.addEventListener('resize', debounce(this.onResize.bind(this)));

    this.mobileMatchMedia.addEventListener('change', this.toggleScrollBinding.bind(this));
    this.toggleScrollBinding(this.mobileMatchMedia);
  }

  toggleScrollBinding(mql) {
    if (mql.matches) {
      this.addEventListener('scroll', this.onScroll.bind(this));
    } else {
      this.removeEventListener('scroll', this.onScroll.bind(this));
    }
  }

  onResize() {
    requestAnimationFrame(() => {
      this.relocateHeader();
      requestAnimationFrame(() => {
        this.setInitialPosition();
      });
    });
  }

  connectedCallback() {
    this.relocateHeader();
    this.initVideos();
  }

  relocateHeader() {
    let relocated = this.galleryMain.contains(this.headerGroup);
    if (!this.mobileMatchMedia.matches) {
      if (relocated) {
        document.querySelector('.a11y-content-link').insertAdjacentElement('afterend', this.headerGroup);
      }
    } else if (!relocated) {
      this.galleryMain.prepend(this.headerGroup);
    }
  }

  onDocumentScroll() {
    if (window.scrollY + window.innerHeight > this.getBoundingClientRect().height) {
      this.view360TriggerDesktop.classList.replace('fixed', 'absolute');
      this.view360TriggerDesktop.classList.replace('bottom-20.5', 'bottom-9');
    } else {
      this.view360TriggerDesktop.classList.replace('absolute', 'fixed');
      this.view360TriggerDesktop.classList.replace('bottom-9', 'bottom-20.5');
    }
  }

  /**
   * Handles breaking out of the scroll trap when the user reaches the bottom of the gallery.
   * - Gets the height of the visible part of the image gallery.
   * - Checks if the user has scrolled past the bottom of the gallery.
   * - Adds overflow hidden then restores
   */
  onScroll() {
    const visibleHeight = this.getBoundingClientRect().height;
    if (this.scrollHeight - (this.scrollTop + visibleHeight) < 0) {
      this.style.overflow = 'hidden';
      setTimeout(() => {
        this.style.overflow = '';
      }, 50);
    }
  }

  onViewMedia() {
    const thisMediaId = event.currentTarget.dataset.mediaId;
    const mediaButton = document.querySelector(`[data-media-id="${thisMediaId}"]`);
    mediaButton.click();
  }

  onScrollToMedia() {
    const thisMediaId = event.currentTarget.dataset.mediaId;
    const mediaButton = document.querySelector(`[data-media-id="${thisMediaId}"]`);
    window.smoothScrollTo(document.scrollingElement || document.documentElement, mediaButton.offsetTop, 300);
  }

  onMouseMove(event) {
    if (!this.mobileMatchMedia.matches || this.isViewing360) {
      return;
    }
    if (this.isDragging) {
      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;
      const newLeft = this.initialX + dx;
      const newTop = this.initialY + dy;

      if (newLeft < 1 && newLeft + this.galleryActiveImage.offsetWidth > window.innerWidth) {
        this.galleryViewerImage.style.left = this.initialX + dx + 'px';
      }

      if (newTop < 1 && newTop + this.galleryActiveImage.offsetHeight > window.innerHeight) {
        this.galleryViewerImage.style.top = this.initialY + dy + 'px';
      }
    }
  }

  onTouchMove(event) {
    if (this.isDragging) {
      const touches = event.changedTouches;
      for (let i = 0; i < touches.length; i++) {
        const dx = touches[i].clientX - this.startX;
        const dy = touches[i].clientY - this.startY;
        const newLeft = this.initialX + dx;
        const newTop = this.initialY + dy;

        if (newLeft < 1 && newLeft + this.galleryActiveImage.offsetWidth > window.innerWidth) {
          this.galleryViewerImage.style.left = this.initialX + dx + 'px';
        }
        if (newTop < 1 && newTop + this.galleryActiveImage.offsetHeight > window.innerHeight) {
          this.galleryViewerImage.style.top = this.initialY + dy + 'px';
        }
      }
    }
  }

  onTouchStart(event) {
    this.isDragging = true;
    this.startX = event.changedTouches[0].clientX;
    this.startY = event.changedTouches[0].clientY;
    this.initialX = this.galleryViewerImage.offsetLeft;
    this.initialY = this.galleryViewerImage.offsetTop;
    this.galleryViewerImage.style.cursor = 'grabbing';
  }

  onMouseUp() {
    if (this.isDragging) {
      this.isDragging = false;
      this.galleryViewerImage.style.cursor = 'grab';
    }
  }

  onMouseDown(event) {
    if (!this.mobileMatchMedia.matches || this.isViewing360) {
      return;
    }
    this.isDragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.initialX = this.galleryViewerImage.offsetLeft;
    this.initialY = this.galleryViewerImage.offsetTop;
    this.galleryViewerImage.style.cursor = 'grabbing';
  }

  onClick(event) {
    if (!this.mobileMatchMedia.matches) {
      this.closeGallery();
    }
  }

  setInitialPosition() {
    if (!this.imageViewerOpen) {
      return;
    }

    if (this.isViewing360 && !this.mobileMatchMedia.matches) {
      let leftPosition = (this.galleryActiveImage.offsetWidth - window.innerWidth) / 2;
      this.galleryViewerImage.style.top = `0px`;
      this.galleryViewerImage.style.left = `${-leftPosition - 7}px`;
    } else if (this.isViewing360 && this.mobileMatchMedia.matches) {
      let topPosition =
        (this.galleryActiveImage.offsetHeight - window.innerHeight) / 2 - this.productGalleryThumbnailsHeight;
      this.galleryViewerImage.style.top = `${-topPosition}px`;
      this.galleryViewerImage.style.left = `0px`;
    } else if (!this.isViewing360 && !this.mobileMatchMedia.matches) {
      let topPosition =
        (this.galleryActiveImage.offsetHeight - window.innerHeight) / 2 - this.productGalleryThumbnailsHeight;
      this.galleryViewerImage.scrollTop = topPosition;
      this.galleryViewerImage.style.left = `0px`;
    } else {
      let topPosition =
        (this.galleryActiveImage.offsetHeight - window.innerHeight) / 2 - this.productGalleryThumbnailsHeight;
      let leftPosition = (this.galleryActiveImage.offsetWidth - window.innerWidth) / 2;
      this.galleryViewerImage.style.top = `${-topPosition}px`;
      this.galleryViewerImage.style.left = `${-leftPosition}px`;
    }
  }

  recalcHeight() {
    if (this.mobileMatchMedia.matches) {
      const elem = document.createElement('div');
      elem.style.visibility = 'hidden';
      elem.style.position = 'absolute';
      elem.top = '0';
      elem.style.height = 'calc(100dvh - (var(--mobile-form-height)))';
      document.body.appendChild(elem);
      requestAnimationFrame(() => {
        const height = elem.offsetHeight + 'px';
        document.body.removeChild(elem);
        this.style.height = height;
      });
    } else {
      this.style.height = '';
    }
  }

  handleThumbnailClick(event) {
    this.galleryActiveImage.classList.add('hidden');
    const thisTarget = event.currentTarget;

    this.galleryActiveImage = this.galleryViewerImage.querySelector(`[data-media-id="${thisTarget.dataset.mediaId}"]`);

    if (thisTarget.dataset.mediaType === 'image') {
      this.isViewing360 = false;
      this.galleryViewerElementImage.classList.remove('is-video');
      this.galleryViewerElementImage.classList.add('is-image');
    } else {
      this.isViewing360 = true;
      // if (this.mobileMatchMedia.matches) {
      //   this.galleryActiveImage.classList.add('w-full');
      //   this.galleryActiveImage.classList.remove('h-screen', 'min-w-fit', 'max-w-fit');
      // }
      this.galleryViewerElementImage.classList.add('is-video');
      this.galleryViewerElementImage.classList.remove('is-image');

      setTimeout(() => {
        this.setInitialPosition();
        this.galleryActiveImage.play();
      }, 100);
    }
    this.setActiveThumbnail(thisTarget.dataset.mediaId);
    this.galleryActiveImage.classList.remove('hidden');

    const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
    this.galleryViewerImage.scrollTop = heightDifference / 2;

    this.setInitialPosition();
  }

  initVideos() {
    const videoStateMap = new Map();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting && !videoStateMap.get(target)) {
          target.play();
          videoStateMap.set(target, true);
        }
      });
    });

    this.galleryVideos.forEach((videoWrap) => {
      videoStateMap.set(videoWrap, false);
      observer.observe(videoWrap);
    });
  }

  openGallery(event) {
    this.galleryTriggerImage = event.currentTarget;
    const mediaId = event.currentTarget.dataset.mediaId;
    this.galleryViewerImage.classList.add('!overflow-hidden');
    this.galleryActiveImage = this.galleryViewerImage.querySelector(`[data-media-id="${mediaId}"]`);

    this.galleryViewerImage.querySelectorAll('img').forEach((image) => {
      image.setAttribute('loading', 'eager');
    });

    if (event.currentTarget.tagName === 'IMG') {
      this.galleryViewerElementImage.classList.remove('is-video');
      this.galleryViewerElementImage.classList.add('is-image');
    } else {
      this.galleryViewerElementImage.classList.add('is-video');
      this.galleryViewerElementImage.classList.remove('is-image');

      if (this.mobileMatchMedia.matches) {
        this.galleryActiveImage.classList.add('w-full');
        this.galleryActiveImage.classList.remove('h-screen', 'min-w-fit', 'max-w-fit');
      } else {
        this.galleryActiveImage.classList.replace('w-full', 'h-screen');
        this.galleryActiveImage.classList.add('min-w-fit', 'max-w-fit');
      }

      this.isViewing360 = true;
    }

    this.galleryActiveImage.classList.remove('hidden');

    this.imageViewerOpen = true;

    this.galleryViewerElement.classList.remove('hidden');

    setTimeout(() => {
      this.setInitialPosition();

      const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
      this.galleryViewerImage.scrollTop = heightDifference / 2;

      this.disableBodyScroll();

      setTimeout(() => {
        this.galleryViewerElement.classList.remove('translate-y-full');

        this.setActiveThumbnail(mediaId);

        waitForTransitionEnd(this.galleryViewerElement).then(() => {
          const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
          this.galleryViewerImage.classList.remove('tablet:p-scrollbar');
          this.galleryViewerImage.scrollTop = heightDifference / 2;
          this.galleryViewerImage.classList.remove('!overflow-hidden');
          this.thumbnailMain.classList.remove('hidden');

          setTimeout(() => {
            this.thumbnailMain.classList.remove('opacity-0');
            this.galleryClose.classList.remove('opacity-0');
            trapFocus(this.galleryViewerElement, this.galleryViewerElement.querySelector('button'));
          }, 100);
        });
      }, 200);
    }, 100);
  }

  setActiveThumbnail(mediaId) {
    const newActiveThumbnail = document.querySelector(`.thumbnail-button[data-media-id="${mediaId}"]`);
    this.activeThumbnail.classList.remove('shadow-[inset_0_0_0_1px_black]');
    this.activeThumbnail.setAttribute('aria-current', false);
    this.activeThumbnail = newActiveThumbnail;
    this.activeThumbnail.classList.add('shadow-[inset_0_0_0_1px_black]');
    this.activeThumbnail.setAttribute('aria-current', true);
  }

  closeGallery() {
    this.thumbnailMain.classList.add('opacity-0');
    this.galleryClose.classList.add('opacity-0');
    this.imageViewerOpen = false;
    this.isViewing360 = false;

    waitForTransitionEnd(this.thumbnailMain).then(() => {
      this.thumbnailMain.classList.add('hidden');
      this.galleryViewerImage.classList.add('!overflow-hidden');

      this.enableBodyScroll();

      setTimeout(() => {
        this.galleryViewerElement.classList.add('translate-y-full');

        waitForTransitionEnd(this.galleryViewerElement).then(() => {
          this.galleryViewerElement.classList.add('hidden');
          this.galleryViewerImage.classList.add('tablet:p-scrollbar');
          this.galleryViewerImage.querySelectorAll('img:not(.hidden)').forEach((image) => {
            image.classList.add('hidden');
          });
          setTimeout(() => {
            releaseFocus();
            if (this.galleryTriggerImage) {
              document
                .querySelector(
                  `.product__media img[data-media-id="${this.galleryTriggerImage.getAttribute('data-media-id')}"]`,
                )
                .focus();
            }
          }, 500);
        });
      }, 66);
    });
  }

  disableBodyScroll() {
    document.documentElement.dataset.pinned = '1';
    this.currentScroll = document.documentElement.scrollTop;
    window.disableBodyScrolling(this.currentScroll);
  }

  enableBodyScroll() {
    document.documentElement.removeAttribute('data-pinned');
    window.enableBodyScrolling(this.currentScroll);
  }
}

customElements.define('product-gallery', ProductGallery);
